import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { enModoSuite, avisarAlAnfitrion, medirYAvisarElAlto } from '../utils/modoSuite';
import {
  Layers,
  PiggyBank,
  FileText,
  FilePlus,
  History,
  FileX,
  FilePlus2,
  DatabaseBackup,
  Palette,
  CheckCircle,
  Settings,
  Building2,
  Shield,
  Key,
  Hash,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  LogOut,
  User,
  Users,
  UserPlus,
  Menu,
  SlidersHorizontal,
  X,
  Package,
  PackagePlus,
  Upload,
  LayoutDashboard,
  Wallet,
  CreditCard,
  Receipt,
  TrendingUp,
  TrendingDown,
  Truck,
  Boxes,
  ClipboardList,
  DollarSign,
  FileCheck,
  FileWarning,
  UserCheck,
  Building,
  ArrowUpRight,
  ArrowDownLeft,
  Mail,
  RefreshCw,
  BarChart3,
  Activity,
  BookOpen,
  Calculator,
  Landmark,
  Scale,
  PieChart,
  Brain,
  UserCog,
  PanelLeftOpen,
  PanelLeftClose,
  Gauge,
  PlugZap
} from 'lucide-react';
import useModulosAvanzados from '../hooks/useModulosAvanzados';
import useAuthStore from '../store/authStore';
import usePageMetaStore from '../store/pageMetaStore';
import dayjs from 'dayjs';
import clsx from 'clsx';
import NotificationsBell from './NotificationsBell';
import { useTemaStore, useTemaNuevo, useMarcaDeAgua } from '../store/temaStore';

// Full navigation definition with permission requirements
const allNavigation = [
  {
    name: 'Facturación',
    icon: FileText,
    modulo: 'facturacion',
    children: [
      { name: 'Crear Factura', href: '/facturacion/nueva', icon: FilePlus },
      { name: 'Facturación Recurrente', href: '/facturacion/recurrentes', icon: RefreshCw },
      { name: 'FinOps', href: '/facturacion/ops', icon: Gauge, moduloAvanzado: 'ops' },
      { name: 'Facturación API', href: '/facturacion/api-drafts', icon: Key },
      { name: 'Historial de Facturas', href: '/facturacion/historial', icon: History },
      { name: 'Notas de Crédito', href: '/facturacion/notas-credito', icon: FileX },
      { name: 'Notas de Débito', href: '/facturacion/notas-debito', icon: FilePlus2 },
      { name: 'Clientes', href: '/clientes', icon: Users },
    ]
  },
  {
    name: 'Gastos',
    icon: Receipt,
    modulo: 'gastos',
    children: [
      { name: 'Bandeja de Entrada', href: '/gastos/bandeja', icon: Mail },
      { name: 'Por Confirmar', href: '/gastos/por-confirmar', icon: FileCheck },
      { name: 'Pendientes de rechazo', href: '/gastos/pendientes-rechazo', icon: FileWarning },
      { name: 'Documentos Aceptados', href: '/gastos/aceptadas', icon: CheckCircle },
      { name: 'FE de Compra', href: '/gastos/fec', icon: FilePlus },
      { name: 'Control de IVA', href: '/gastos/iva', icon: TrendingUp },
    ]
  },
  {
    name: 'Cotizaciones y Compras',
    icon: FileCheck,
    modulo: 'cotizaciones',
    children: [
      { name: 'Cotizaciones', href: '/facturacion/cotizaciones', icon: FileCheck },
      { name: 'Ordenes de Compra', href: '/compras/ordenes-compra', icon: ClipboardList },
    ]
  },
  {
    name: 'Cuentas x Cobrar',
    icon: ArrowUpRight,
    modulo: 'cxc',
    children: [
      { name: 'Facturas Pendientes', href: '/cxc/pendientes', icon: FileText },
      { name: 'Por Cliente', href: '/cxc/por-cliente', icon: Users },
      { name: 'Histórico de Cobros', href: '/cxc/historico', icon: History },
    ]
  },
  {
    name: 'Cuentas x Pagar',
    icon: ArrowDownLeft,
    modulo: 'cxp',
    children: [
      { name: 'Facturas por Pagar', href: '/cxp/facturas', icon: FileText },
      { name: 'Por Proveedor', href: '/cxp/por-proveedor', icon: Truck },
      { name: 'Lotes de Pago', href: '/cxp/lotes', icon: Layers },
      { name: 'Pagos del Banco', href: '/cxp/pagos-banco', icon: Upload },
      { name: 'Histórico de Pagos', href: '/cxp/historico-pagos', icon: History },
      { name: 'Proveedores', href: '/cxp/proveedores', icon: Truck },
    ]
  },
  {
    name: 'Inventarios',
    icon: Boxes,
    modulo: 'inventarios',
    children: [
      { name: 'Productos', href: '/productos', icon: Package },
      { name: 'Importar Productos', href: '/productos/importar', icon: Upload },
    ]
  },
  {
    name: 'Planillas',
    icon: UserCog,
    modulo: 'planillas',
    requiresEmisorFlag: 'planillasEnabled', // visible solo si el emisor activó el módulo externo
    children: [
      { name: 'Payroll360 Lite', href: '/planillas', icon: UserCog },
    ]
  },
  {
    name: 'Contabilidad',
    icon: DollarSign,
    modulo: 'contabilidad',
    children: [
      { name: 'Dashboard', href: '/contabilidad', icon: LayoutDashboard },
      { name: 'Plan de Cuentas', href: '/contabilidad/cuentas', icon: ClipboardList },
      { name: 'Asientos', href: '/contabilidad/asientos', icon: FileText },
      { name: 'Libro Mayor', href: '/contabilidad/mayor', icon: History },
      { name: 'Balance General', href: '/contabilidad/balance', icon: TrendingUp },
      { name: 'Balance de Comprobación', href: '/contabilidad/comprobacion', icon: Scale },
      { name: 'Estado Resultados', href: '/contabilidad/resultados', icon: TrendingDown },
      { name: 'Bancos', href: '/contabilidad/bancos', icon: Building },
      { name: 'Reportes Hacienda', href: '/contabilidad/hacienda', icon: FileText },
      { name: 'Dimensiones', href: '/contabilidad/dimensiones', icon: Layers },
      { name: 'Activos Fijos', href: '/contabilidad/activos', icon: Package },
      { name: 'Préstamos', href: '/contabilidad/prestamos', icon: Landmark },
      { name: 'Flujo de Caja', href: '/contabilidad/flujo-caja', icon: TrendingUp },
      { name: 'Comparativos', href: '/contabilidad/comparativos', icon: BarChart3 },
      { name: 'Indicadores', href: '/contabilidad/indicadores', icon: Gauge, moduloAvanzado: 'agro' },
      { name: 'Diferencial Cambiario', href: '/contabilidad/diferencial-cambiario', icon: DollarSign },
      { name: 'Configuración', href: '/contabilidad/config', icon: Settings },
    ]
  },
  {
    name: 'Presupuesto',
    icon: PiggyBank,
    modulo: 'presupuesto',
    children: [
      { name: 'Presupuesto anual', href: '/presupuesto', icon: PiggyBank },
    ]
  },
  {
    name: 'Reportes',
    icon: BarChart3,
    modulo: 'reportes',
    children: [
      { name: 'Centro de Reportes', href: '/reportes', icon: BarChart3 },
      { name: 'Reportes Personalizados', href: '/reportes/personalizados', icon: Settings },
    ]
  },
  {
    name: 'Configuración',
    icon: Settings,
    modulo: 'configuracion',
    children: [
      { name: 'Empresas', href: '/configuracion/empresa', icon: Building2 },
      { name: 'Parámetros', href: '/configuracion/parametros', icon: SlidersHorizontal },
      { name: 'Usuarios', href: '/configuracion/usuarios', icon: Users },
      { name: 'Roles', href: '/configuracion/roles', icon: Shield },
      { name: 'Credenciales API', href: '/configuracion/api-credentials', icon: Key },
      { name: 'Integraciones', href: '/configuracion/integraciones', icon: PlugZap },
      { name: 'Estado Sistema', href: '/configuracion/estado-sistema', icon: Activity },
      { name: 'Respaldos', href: '/configuracion/respaldos', icon: DatabaseBackup },
      { name: 'Apariencia', href: '/configuracion/apariencia', icon: Palette },
    ]
  }
];

// Helper to filter navigation based on user permissions + emisor feature flags
const getFilteredNavigation = (user, currentEmisor, modulosAvanzados = []) => {
  const nav = filtrarPorPermisos(user, currentEmisor);
  // Opciones de un Módulo Avanzado: solo si el super admin se lo asignó a la empresa
  return nav.map(section => ({ ...section, children: section.children.filter(c => !c.moduloAvanzado || modulosAvanzados.includes(c.moduloAvanzado)) }))
    .filter(section => section.children.length);
};

const filtrarPorPermisos = (user, currentEmisor) => {
  // Helper: ¿el item requiere un flag activado en el emisor?
  const passesEmisorFlag = (section) => {
    if (!section.requiresEmisorFlag) return true;
    return !!currentEmisor?.[section.requiresEmisorFlag];
  };

  // Admin or old 'admin' role has full access (pero igualmente respeta requiresEmisorFlag)
  if (user?.role === 'admin') {
    return allNavigation.filter(passesEmisorFlag);
  }

  // If user has permisos array, filter based on it
  const permisos = user?.permisos || [];
  if (permisos.length === 0) return [];

  // Get unique modules the user has access to
  const allowedModules = new Set(permisos.map(p => p.modulo));

  // Filter: doble chequeo permiso + flag del emisor
  return allNavigation.filter(section =>
    allowedModules.has(section.modulo) && passesEmisorFlag(section)
  );
};

function NavSection({ section, isOpen, onToggle, onNavigate, tema }) {
  const location = useLocation();
  const isActive = section.children.some(child => location.pathname.startsWith(child.href));
  const isSectionDisabled = section.disabled;

  const handleClick = () => {
    if (isSectionDisabled) return;

    // Si no está abierto, abrir y navegar al primer item disponible
    if (!isOpen) {
      const firstAvailable = section.children.find(child => !child.disabled);
      if (firstAvailable) {
        onNavigate(firstAvailable.href);
      }
    }
    onToggle();
  };

  // Temas Finance360: el aspecto lo da estilos/finance.css (clases grupo/on/abierto)
  if (tema) {
    return (
      <div>
        <button onClick={handleClick} className={clsx('grupo', isActive && 'on', isOpen && 'abierto', isSectionDisabled && 'apagado')}>
          <span className="flex items-center gap-3">
            <section.icon />
            <span>{section.name}</span>
          </span>
          {!isSectionDisabled && <ChevronRight className="flecha" />}
        </button>
        {isOpen && !isSectionDisabled && (
          <div className="f360-sub space-y-0.5">
            {section.children.map((item) => item.disabled ? (
              <span key={item.name} className="apagado flex items-center gap-3 px-2.5 py-1.5 text-[13.5px]" title="Próximamente">
                <item.icon className="h-4 w-4" /><span>{item.name}</span>
              </span>
            ) : (
              <Link key={item.name} to={item.href} className={clsx(location.pathname === item.href && 'on')}>
                <item.icon />
                <span>{item.name}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mb-1">
      <button
        onClick={handleClick}
        className={clsx(
          'w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg transition-colors',
          isSectionDisabled
            ? 'text-gray-400 cursor-not-allowed'
            : isActive
              ? 'bg-blue-50 text-blue-700'
              : 'text-gray-700 hover:bg-gray-100'
        )}
      >
        <div className="flex items-center gap-3">
          <section.icon className="h-5 w-5" />
          <span>{section.name}</span>
          {isSectionDisabled && (
            <span className="text-[10px] bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded">Pronto</span>
          )}
        </div>
        {!isSectionDisabled && (
          isOpen ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )
        )}
      </button>

      {isOpen && !isSectionDisabled && (
        <div className="mt-1 ml-4 pl-4 border-l border-gray-200 space-y-1">
          {section.children.map((item) => {
            const isItemActive = location.pathname === item.href;

            // Disabled items (coming soon)
            if (item.disabled) {
              return (
                <div
                  key={item.name}
                  className="flex items-center gap-3 px-3 py-2 text-sm rounded-lg text-gray-400 cursor-not-allowed"
                  title="Próximamente"
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.name}</span>
                  <span className="ml-auto text-[10px] bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded">Pronto</span>
                </div>
              );
            }

            return (
              <Link
                key={item.name}
                to={item.href}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors',
                  isItemActive
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                )}
              >
                <item.icon className="h-4 w-4" />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Helper function to find section by path
const findSectionByPath = (path, navItems) => {
  // Dashboard no pertenece a ningún módulo
  if (path === '/' || path === '') {
    return null;
  }

  // First try exact match (most specific wins)
  for (const section of navItems) {
    const hasExactChild = section.children?.some(child => path.startsWith(child.href));
    if (hasExactChild) {
      return section.name;
    }
  }

  // Fallback: match by first path segment
  for (const section of navItems) {
    const hasActiveChild = section.children?.some(child => {
      const childBase = '/' + child.href.split('/')[1];
      const pathBase = '/' + path.split('/')[1];
      return childBase === pathBase;
    });
    if (hasActiveChild) {
      return section.name;
    }
  }
  return null; // No default, ningún módulo abierto
};

// Mapa de títulos de página por ruta
const PAGE_TITLES = {
  '/facturacion/historial': { title: 'Comprobantes Electrónicos', icon: History },
  '/facturacion/nueva': { title: 'Nueva Factura', icon: FilePlus },
  '/facturacion/recurrentes': { title: 'Facturación Recurrente', icon: RefreshCw },
  '/facturacion/ops': { title: 'FinOps', icon: Gauge },
  '/facturacion/notas-credito': { title: 'Notas de Crédito', icon: FileX },
  '/facturacion/notas-debito': { title: 'Notas de Débito', icon: FilePlus2 },
  '/facturacion/cotizaciones': { title: 'Cotizaciones', icon: FileCheck },
  '/compras/ordenes-compra': { title: 'Órdenes de Compra', icon: ClipboardList },
  '/clientes': { title: 'Clientes', icon: Users },
  '/gastos/bandeja': { title: 'Bandeja de Entrada', icon: Mail },
  '/gastos/por-confirmar': { title: 'Facturas por Confirmar', icon: FileCheck },
  '/gastos/pendientes-rechazo': { title: 'Pendientes de rechazo al proveedor', icon: FileWarning },
  '/gastos/aceptadas': { title: 'Documentos Aceptados', icon: CheckCircle },
  '/gastos/fec': { title: 'Factura Electrónica de Compra', icon: FilePlus },
  '/gastos/iva': { title: 'Control de IVA', icon: TrendingUp },
  '/cxc/pendientes': { title: 'Facturas Pendientes', icon: FileText },
  '/cxc/por-cliente': { title: 'Cuentas por Cliente', icon: Users },
  '/cxc/historico': { title: 'Histórico de Cobros', icon: History },
  '/cxp/facturas': { title: 'Facturas por Pagar', icon: FileText },
  '/cxp/por-proveedor': { title: 'Cuentas por Proveedor', icon: Truck },
  '/cxp/lotes': { title: 'Lotes de Pago', icon: Layers },
  '/cxp/pagos-banco': { title: 'Pagos del Banco', icon: Upload },
  '/cxp/historico-pagos': { title: 'Histórico de Pagos', icon: History },
  '/cxp/proveedores': { title: 'Proveedores', icon: Truck },
  '/productos': { title: 'Productos / Servicios', icon: Package },
  '/productos/nuevo': { title: 'Nuevo Producto', icon: PackagePlus },
  '/productos/importar': { title: 'Importar Productos', icon: Upload },
  '/configuracion/empresas': { title: 'Empresas', icon: Building },
  '/configuracion/parametros': { title: 'Parámetros', icon: Settings },
  '/configuracion/empresa': { title: 'Datos de la Empresa', icon: Building2 },
  '/configuracion/certificado': { title: 'Certificado Digital', icon: Shield },
  '/configuracion/hacienda': { title: 'Configuración Hacienda', icon: Key },
  '/configuracion/consecutivos': { title: 'Consecutivos', icon: Hash },
  '/configuracion/usuarios': { title: 'Usuarios', icon: UserPlus },
  '/configuracion/roles': { title: 'Roles y Permisos', icon: UserCheck },
  '/configuracion/email': { title: 'Configuración Email', icon: Mail },
  '/configuracion/estado-sistema': { title: 'Estado del Sistema', icon: Activity },
  '/configuracion/respaldos': { title: 'Respaldos', icon: DatabaseBackup },
  '/configuracion/apariencia': { title: 'Apariencia', icon: Palette },
  '/configuracion/api-credentials': { title: 'Credenciales API', icon: Key },
  '/configuracion/integraciones': { title: 'Integraciones', icon: PlugZap },
  '/reportes': { title: 'Centro de Reportes', icon: BarChart3 },
  '/reportes/personalizados': { title: 'Reportes Personalizados', icon: Settings },
  '/reportes/dashboard': { title: 'Dashboard', icon: LayoutDashboard },
  '/reportes/ventas': { title: 'Reporte de Ventas', icon: BarChart3 },
  '/facturacion/api-drafts': { title: 'Facturación API', icon: Key },
  '/presupuesto': { title: 'Presupuesto', icon: PiggyBank },
  // Contabilidad
  '/contabilidad': { title: 'Contabilidad', icon: BookOpen },
  '/contabilidad/cuentas': { title: 'Plan de Cuentas', icon: Landmark },
  '/contabilidad/asientos': { title: 'Asientos Contables', icon: Calculator },
  '/contabilidad/mayor': { title: 'Libro Mayor', icon: BookOpen },
  '/contabilidad/balance': { title: 'Balance General', icon: Scale },
  '/contabilidad/comprobacion': { title: 'Balance de Comprobación', icon: Scale },
  '/contabilidad/resultados': { title: 'Estado de Resultados', icon: PieChart },
  '/contabilidad/bancos': { title: 'Cuentas Bancarias', icon: Landmark },
  '/contabilidad/hacienda': { title: 'Reportes Hacienda', icon: FileText },
  '/contabilidad/dimensiones': { title: 'Dimensiones Contables', icon: Layers },
  '/contabilidad/activos': { title: 'Activos Fijos', icon: Package },
  '/contabilidad/prestamos': { title: 'Préstamos', icon: Landmark },
  '/contabilidad/flujo-caja': { title: 'Flujo de Caja Proyectado', icon: TrendingUp },
  '/contabilidad/comparativos': { title: 'Reportes Comparativos', icon: BarChart3 },
  '/contabilidad/indicadores': { title: 'Indicadores por Unidad', icon: Gauge },
  '/contabilidad/diferencial-cambiario': { title: 'Diferencial Cambiario', icon: DollarSign },
  '/contabilidad/config': { title: 'Configuración Contable', icon: Settings },
};

/**
 * El título de la ruta: la exacta o, si no hay, la más larga que la contenga
 * (`/contabilidad/prestamos/7` → Préstamos). Antes solo se buscaba la exacta y toda
 * pantalla de detalle decía «Dashboard» (encontrado en F16, 25/10).
 */
function tituloDeRuta(path) {
  if (PAGE_TITLES[path]) return PAGE_TITLES[path];
  const base = Object.keys(PAGE_TITLES).filter(k => path.startsWith(`${k}/`)).sort((a, b) => b.length - a.length)[0];
  return base ? PAGE_TITLES[base] : null;
}

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  // ── LA FRONTERA DEL MODO SUITE (item 096) ───────────────────────────────
  //
  // Esto va PRIMERO y antes de cualquier otro hook a proposito. Cuando Finance
  // corre dentro de Unity, el armazon NO se dibuja: ni barra lateral, ni
  // encabezado, ni «Salir». Y la frontera esta aca —donde se dibuja el
  // armazon— y no en la lista blanca de `EmbedShell`, porque una lista obliga a
  // enumerar cada pantalla y falla por omision con la que alguien agregue
  // mañana. Un interruptor en el armazon cubre todas las rutas que hay y todas
  // las que vengan.
  //
  // Tambien se le avisa al anfitrion a donde se fue, para que la direccion de
  // Unity refleje lo que se esta viendo y un enlace directo vuelva aca.
  const esSuite = enModoSuite();
  const contenidoSuite = useRef(null);
  useEffect(() => {
    if (esSuite) avisarAlAnfitrion('embed:ruta', { ruta: location.pathname + location.search });
  }, [esSuite, location.pathname, location.search]);
  // El alto lo mide el armazon de aca en adelante: la cascara que lo media se
  // desmonto al navegar, y por eso el marco se quedaba con el alto de la
  // pantalla de entrada. Ver `medirYAvisarElAlto`.
  useEffect(() => {
    if (!esSuite || !contenidoSuite.current) return;
    return medirYAvisarElAlto(contenidoSuite.current);
  }, [esSuite, location.pathname]);
  const { user, currentEmisor, emisores, setCurrentEmisor, logout, pageStatus, tipoCambio, fetchTipoCambio } = useAuthStore();
  // Metadatos transitorios publicados por la página activa (ej. "Actualizado: HH:MM:SS")
  const pageLastUpdated = usePageMetaStore(s => s.lastUpdated);
  const clearPageMeta = usePageMetaStore(s => s.clear);
  // Limpiar pageMeta al cambiar de ruta — evita arrastrar la hora a páginas que no la setean
  useEffect(() => { clearPageMeta(); }, [location.pathname, clearPageMeta]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showEmisorMenu, setShowEmisorMenu] = useState(false);
  // Tema de la cuenta (Configuración → Apariencia): finance360 | finance360-solido | clasico
  const temaNuevo = useTemaNuevo();
  const marcaDeAgua = useMarcaDeAgua();
  const cargarTema = useTemaStore((st) => st.cargar);
  useEffect(() => { cargarTema(); }, [cargarTema]);
  // Rutas embebidas (iframe a pantalla completa) — el sidebar se oculta cuando el
  // sistema embebido (Payroll360 Lite, etc.) avisa que el usuario está logueado
  // (vía postMessage). Arranca visible: aparece login del sistema externo, user se loguea,
  // el sistema externo notifica y recién ahí ocultamos para dar todo el ancho.
  const EMBED_ROUTES = ['/planillas'];
  const isEmbedRoute = EMBED_ROUTES.includes(location.pathname);
  const [desktopSidebarHidden, setDesktopSidebarHidden] = useState(false);
  // Preferencia manual del usuario para ocultar el sidebar (persistida). Independiente del
  // auto-ocultado de rutas embebidas.
  const [userSidebarHidden, setUserSidebarHidden] = useState(() => localStorage.getItem('sidebarHidden') === '1');
  const toggleUserSidebar = (hidden) => {
    setUserSidebarHidden(hidden);
    localStorage.setItem('sidebarHidden', hidden ? '1' : '0');
    if (!hidden) setDesktopSidebarHidden(false);
  };
  // Estado efectivo: oculto si el usuario lo pidió o si una ruta embebida lo auto-oculta.
  const sidebarHidden = userSidebarHidden || desktopSidebarHidden;
  // 27/10/2026: «ocultar» ya no esconde todo. Lo que pide el usuario es la franja de íconos (riel); solo
  // las rutas embebidas (iframe a pantalla completa) esconden el menú entero.
  const sidebarOculto = desktopSidebarHidden;
  const sidebarRiel = userSidebarHidden && !desktopSidebarHidden;
  // Panel flotante del riel: la sección bajo el mouse y a qué altura se abre.
  const [riel, setRiel] = useState(null);
  // Al salir el mouse el panel no desaparece de golpe: se cierra con la animación inversa (como cerrar un libro).
  const [rielCerrando, setRielCerrando] = useState(false);
  const rielCierre = useRef(null);
  const RIEL_CIERRE_MS = 450;
  // El panel que se va cerrando cuando se pasa de un ícono a otro (se cierra mientras el nuevo se abre).
  const [rielSaliente, setRielSaliente] = useState(null);
  const rielSalida = useRef(null);
  const abrirRiel = (nombre, e) => {
    clearTimeout(rielCierre.current);
    setRielCerrando(false);
    const r = e.currentTarget.getBoundingClientRect();
    if (riel && riel.nombre !== nombre) {
      clearTimeout(rielSalida.current);
      setRielSaliente(riel);
      rielSalida.current = setTimeout(() => setRielSaliente(null), RIEL_CIERRE_MS);
    }
    setRiel({ nombre, top: Math.min(r.top, window.innerHeight - 320) });
  };
  const cerrarRiel = () => {
    clearTimeout(rielCierre.current);
    rielCierre.current = setTimeout(() => {
      setRielCerrando(true);
      rielCierre.current = setTimeout(() => { setRiel(null); setRielCerrando(false); }, RIEL_CIERRE_MS);
    }, 180);
  };
  const seguirEnRiel = () => { clearTimeout(rielCierre.current); setRielCerrando(false); };

  // Get filtered navigation based on user permissions + emisor feature flags
  const modulosAvanzados = useModulosAvanzados(currentEmisor?.id);
  const navigation = useMemo(
    () => getFilteredNavigation(user, currentEmisor, modulosAvanzados || []),
    [user?.permisos, user?.role, currentEmisor?.id, currentEmisor?.planillasEnabled, modulosAvanzados]
  );

  // Inicializar con la sección correcta basada en la URL actual
  const [openSections, setOpenSections] = useState(() => {
    const section = findSectionByPath(location.pathname, navigation);
    return section ? [section] : [];
  });

  // Fetch exchange rate once when entering the system
  useEffect(() => {
    if (!tipoCambio) {
      fetchTipoCambio();
    }
  }, []);

  // Auto-abrir la sección correcta según la URL actual (cuando cambia la ruta)
  useEffect(() => {
    const sectionName = findSectionByPath(location.pathname, navigation);
    setOpenSections(sectionName ? [sectionName] : []);
  }, [location.pathname, navigation]);

  // Cuando salís de una ruta embebida, siempre volver a mostrar el sidebar
  // (defensivo: por si el sistema externo nos había dicho "login" pero olvidó el "logout").
  useEffect(() => {
    if (!isEmbedRoute) {
      setDesktopSidebarHidden(false);
    }
  }, [location.pathname]);

  // Escuchar postMessage del iframe embebido (Payroll360 Lite, etc.).
  // Solo aceptamos mensajes de orígenes confiables (la URL configurada en planillas_url).
  useEffect(() => {
    const trustedOrigins = new Set();
    try {
      if (currentEmisor?.planillasUrl) {
        trustedOrigins.add(new URL(currentEmisor.planillasUrl).origin);
      }
    } catch { /* URL inválida, ignorar */ }

    const handleMessage = (event) => {
      if (!trustedOrigins.has(event.origin)) return;
      const { type } = event.data || {};
      if (type === 'payroll360:login') {
        setDesktopSidebarHidden(true);
      } else if (type === 'payroll360:logout') {
        setDesktopSidebarHidden(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [currentEmisor?.planillasUrl]);

  const toggleSection = (sectionName) => {
    setOpenSections(prev =>
      prev.includes(sectionName)
        ? [] // Si ya está abierto, cerrar todo
        : [sectionName] // Si no, abrir solo este (cierra los demás)
    );
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleEmisorChange = (emisor) => {
    if (emisor.id === currentEmisor?.id) { setShowEmisorMenu(false); return; }
    setCurrentEmisor(emisor);
    setShowEmisorMenu(false);
    window.location.href = '/reportes/dashboard';
  };

  // 27/10/2026: al entrar a una pantalla (también al recargar con F5), el menú baja solo hasta la opción activa
  // si no está a la vista; si ya se ve, no se mueve.
  useEffect(() => {
    const t = setTimeout(() => {
      document.querySelectorAll('nav.f360-nav').forEach(nav => {
        if (nav.offsetParent === null) return;
        const on = nav.querySelector('.f360-sub a.on') || nav.querySelector('a.on');
        if (!on) return;
        const n = nav.getBoundingClientRect(); const o = on.getBoundingClientRect();
        if (o.top < n.top || o.bottom > n.bottom) nav.scrollTop += o.top - n.top - n.height / 2 + o.height / 2;
      });
    }, 50);
    return () => clearTimeout(t);
  }, [location.pathname, sidebarRiel]);

  // Efecto del panel del riel: las opciones salen en abanico desde el ícono (probados suave, pliegue y sin efecto).
  const EFECTO_RIEL = 'abanico';

  // Riel: el menú angosto con solo los íconos. Al pasar el mouse por una sección, sus opciones salen en un
  // panel al lado. Se llama como función ({RielContent()}) por lo mismo que SidebarContent.
  const RielContent = () => {
    const activo = (sec) => sec.children.some(ch => location.pathname.startsWith(ch.href));
    const seccion = riel && navigation.find(x => x.name === riel.nombre);
    // Con los temas Finance360 los colores salen de la hoja del tema (clases f360-nav / grupo / on), igual que
    // en el menú completo; sin tema, colores fijos.
    const icono = 'flex items-center justify-center h-10 w-10 rounded-lg transition-colors';
    const tonos = temaNuevo
      ? { base: 'grupo f360-tecla', on: 'grupo on f360-tecla', borde: 'f360-borde', nav: 'f360-nav f360-riel' }
      : { base: 'text-gray-600 hover:bg-gray-100', on: 'bg-blue-50 text-blue-700', borde: 'border-gray-200', nav: '' };
    // Un panel de opciones: el de la sección abierta, o el que se está cerrando (saliendo = true).
    const panelRiel = (seccion, pos, saliendo, esSaliente = false) => !seccion ? null : (
          <div onMouseEnter={esSaliente ? undefined : seguirEnRiel} onMouseLeave={esSaliente ? undefined : cerrarRiel}
            key={`${seccion.name}${esSaliente ? '-sale' : ''}`} className={clsx(EFECTO_RIEL && `f360-efecto-${EFECTO_RIEL}${saliendo ? '-cierre' : ''}`, 'fixed left-16 z-[60] ml-2 w-64 bg-white border border-gray-200 rounded-xl shadow-xl py-2')}
            style={{ top: pos.top, pointerEvents: esSaliente ? 'none' : undefined }}>
            <p className="px-4 pt-1 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{seccion.name}</p>
            {seccion.children.map((item, i) => item.disabled ? (
              <span key={item.name} style={{ animationDelay: `${60 + i * 35}ms` }} className="f360-riel-item flex items-center gap-3 px-4 py-2 text-sm text-gray-400 cursor-not-allowed" title="Próximamente">
                <item.icon className="h-4 w-4" /><span>{item.name}</span>
              </span>
            ) : (
              <Link key={item.name} to={item.href} onClick={() => setRiel(null)} style={{ animationDelay: `${60 + i * 35}ms` }}
                className={clsx('f360-riel-item flex items-center gap-3 px-4 py-2 text-sm transition-colors', location.pathname === item.href ? 'text-indigo-700 font-semibold bg-indigo-50' : 'text-gray-700 hover:bg-gray-50')}>
                <item.icon className="h-4 w-4" /><span>{item.name}</span>
              </Link>
            ))}
          </div>
    );
    return (
      <div className="flex flex-col h-full items-center">
        <Link to="/dashboard" aria-label="Dashboard" className="pt-5 pb-4">
          <img src="/marca/f360-icono-2026.svg" alt="Finance360" className="h-8 w-8" />
        </Link>
        <nav className={clsx('flex-1 w-full flex flex-col items-center gap-1 py-3 overflow-y-auto border-t', tonos.borde, tonos.nav)}>
          <Link to="/dashboard" title="Dashboard" className={clsx(icono, temaNuevo && 'f360-tecla', location.pathname === '/dashboard' ? (temaNuevo ? 'on' : tonos.on) : (temaNuevo ? '' : tonos.base))}>
            <LayoutDashboard className="h-5 w-5" />
          </Link>
          {navigation.map(sec => (
            <button key={sec.name} type="button" title={sec.name} disabled={sec.disabled}
              onMouseEnter={(e) => !sec.disabled && abrirRiel(sec.name, e)} onMouseLeave={cerrarRiel}
              onClick={(e) => { if (sec.disabled) return; if (riel?.nombre === sec.name) setRiel(null); else abrirRiel(sec.name, e); }}
              className={clsx(icono, sec.disabled ? 'opacity-40 cursor-not-allowed' : (activo(sec) || riel?.nombre === sec.name) ? tonos.on : tonos.base)}>
              <sec.icon className="h-5 w-5" />
            </button>
          ))}
        </nav>
        <div className={clsx('w-full flex flex-col items-center gap-1 py-3 border-t', tonos.borde, tonos.nav)}>
          <button type="button" onClick={() => toggleUserSidebar(false)} title="Mostrar el menú completo" aria-label="Mostrar el menú completo" className={clsx(icono, tonos.base)}>
            <PanelLeftOpen className="h-5 w-5" />
          </button>
          <button type="button" onClick={handleLogout} title="Cerrar sesión" aria-label="Cerrar sesión" className={clsx(icono, tonos.base)}>
            <LogOut className="h-5 w-5" />
          </button>
        </div>
        {rielSaliente && rielSaliente.nombre !== riel?.nombre && panelRiel(navigation.find(x => x.name === rielSaliente.nombre), rielSaliente, true, true)}
        {seccion && panelRiel(seccion, riel, rielCerrando)}
      </div>
    );
  };

  // Sidebar content (reused for mobile and desktop).
  // 27/10/2026: se llama como función, {SidebarContent()}, y no como componente <SidebarContent />. Al estar
  // definido dentro de Layout, cada render de Layout creaba un componente nuevo: React desmontaba y volvía a
  // montar todo el menú y su scroll volvía arriba (p. ej. al cargar Flujo de caja, el menú subía a Contabilidad).
  const SidebarContent = () => temaNuevo ? (
    <>
      <div className="f360-regla" />
      <div className="f360-marca flex flex-col items-center px-3 pt-5 pb-4 relative">
        <Link to="/dashboard" aria-label="Ir al dashboard">
          <img src={marcaDeAgua ? '/marca/f360-logo-color-2026.svg' : '/marca/f360-logo-blanco-2026.svg'} alt="Finance360 by Kuanta Bridge" />
        </Link>
        <span className="f360-rotulo mt-2">Consola financiera</span>
        <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1 rounded absolute right-3 top-4" aria-label="Cerrar menú">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="f360-usuario px-4 py-3 border-y">
        <div className="flex items-center gap-3">
          <div className="avatar h-9 w-9 rounded-full flex items-center justify-center">
            <User className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="nombre text-sm font-semibold truncate">{user?.name || 'Usuario'} {user?.lastName || ''}</p>
            <p className="correo text-xs truncate">{user?.email}</p>
          </div>
        </div>
      </div>

      <nav className="f360-nav flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
        <Link to="/dashboard" className={clsx(location.pathname === '/dashboard' && 'on')}>
          <LayoutDashboard />
          <span>Dashboard</span>
        </Link>
        <div className="f360-separador flex items-center gap-2 my-3 px-3"><i /><span>Módulos</span><i /></div>
        {navigation.map((section) => (
          <NavSection
            key={section.name}
            section={section}
            tema
            isOpen={openSections.includes(section.name)}
            onToggle={() => toggleSection(section.name)}
            onNavigate={(href) => navigate(href)}
          />
        ))}
      </nav>

      <div className="f360-pie p-3 border-t">
        <button onClick={handleLogout} className="f360-salir w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg transition-colors">
          <LogOut className="h-4 w-4" />
          <span className="text-sm font-medium">Cerrar sesión</span>
        </button>
      </div>
    </>
  ) : (
    <>
      {/* Logo */}
      <div className="flex items-center justify-center px-3 py-8 border-b border-gray-200 relative">
        <Link to="/dashboard" className="flex items-center gap-3">
          {/* Icon with gradient border */}
          <div
            className="rounded-xl p-[3px] flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, #0D6E6E 0%, #7CB342 25%, #F5A623 50%, #E53935 75%, #1E88E5 100%)'
            }}
          >
            <div className="bg-white rounded-lg p-2.5">
              <svg className="w-9 h-9 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
          {/* Text */}
          <div>
            <div className="text-2xl font-bold text-gray-900 leading-none">Finance 360</div>
            <div className="text-xs font-medium text-gray-500 text-right pr-0.5">by <span className="shimmer-text">Kuanta Bridge</span></div>
          </div>
        </Link>
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden p-1 rounded hover:bg-gray-100 absolute right-3"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* User info - at top before navigation */}
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
            <User className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {user?.name || 'Usuario'} {user?.lastName || ''}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {user?.email}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {/* Dashboard link - destacado */}
        <Link
          to="/dashboard"
          className={clsx(
            'flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors',
            location.pathname === '/dashboard'
              ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-sm'
              : 'text-gray-700 hover:bg-gray-100'
          )}
        >
          <LayoutDashboard className="h-5 w-5" />
          <span>Dashboard</span>
        </Link>

        {/* Separator between Dashboard and modules */}
        <div className="my-4 px-3">
          <div className="flex items-center gap-2">
            <div className="h-px flex-1 bg-gray-200"></div>
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Módulos</span>
            <div className="h-px flex-1 bg-gray-200"></div>
          </div>
        </div>

        {navigation.map((section) => (
          <NavSection
            key={section.name}
            section={section}
            isOpen={openSections.includes(section.name)}
            onToggle={() => toggleSection(section.name)}
            onNavigate={(href) => navigate(href)}
          />
        ))}
      </nav>

      {/* Logout button */}
      <div className="p-3 border-t border-gray-200">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
        >
          <LogOut className="h-4 w-4" />
          <span className="text-sm font-medium">Cerrar sesión</span>
        </button>
      </div>
    </>
  );

  // Sesión de soporte (entrada desde la consola SuperAdmin): se marca siempre a la vista
  let soporte = null;
  try {
    const t = localStorage.getItem('token');
    const payload = t ? JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) : null;
    if (payload?.isImpersonation) soporte = { vence: payload.exp ? new Date(payload.exp * 1000) : null, email: payload.email };
  } catch { /* token sin formato JWT */ }

  // Selector de empresa (lo usan la cabecera clásica y la de los temas Finance360)
  const selectorEmpresa = currentEmisor && (

              <div className="relative">
                <button
                  onClick={() => emisores?.length > 1 && setShowEmisorMenu(!showEmisorMenu)}
                  className={clsx(
                    'flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors',
                    emisores?.length > 1
                      ? 'border-gray-200 hover:bg-gray-50 cursor-pointer'
                      : 'border-transparent cursor-default'
                  )}
                >
                  <div className={clsx(
                    'h-8 w-8 rounded-lg flex items-center justify-center overflow-hidden',
                    currentEmisor.logo_url ? 'bg-white border border-gray-200' : 'bg-gray-100'
                  )}>
                    {currentEmisor.logo_url ? (
                      <img src={currentEmisor.logo_url} alt="" className="h-7 w-7 object-contain" />
                    ) : (
                      <Building2 className="h-4 w-4 text-gray-500" />
                    )}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{currentEmisor.nombre}</p>
                    <p className="text-xs text-gray-500">{currentEmisor.identificacion}</p>
                  </div>
                  {emisores?.length > 1 && (
                    <ChevronDown className={clsx('h-4 w-4 text-gray-400 transition-transform ml-1', showEmisorMenu && 'rotate-180')} />
                  )}
                </button>

                {showEmisorMenu && emisores?.length > 1 && (
                  <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[280px] max-h-64 overflow-y-auto z-50">
                    {[...emisores].sort((a, b) => {
                      const order = { '02': 0, '03': 1, '04': 2, '01': 3 };
                      const oa = order[a.tipoIdentificacion || a.tipo_identificacion] ?? 2;
                      const ob = order[b.tipoIdentificacion || b.tipo_identificacion] ?? 2;
                      return oa - ob || (a.nombre || '').localeCompare(b.nombre || '');
                    }).map((emisor) => (
                      <button
                        key={emisor.id}
                        onClick={() => handleEmisorChange(emisor)}
                        className={clsx(
                          'w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors',
                          currentEmisor?.id === emisor.id && 'bg-blue-50'
                        )}
                      >
                        <div className={clsx(
                          'h-8 w-8 rounded-lg flex items-center justify-center overflow-hidden',
                          emisor.logo_url ? 'bg-white border border-gray-200' : (currentEmisor?.id === emisor.id ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500')
                        )}>
                          {emisor.logo_url ? (
                            <img src={emisor.logo_url} alt="" className="h-7 w-7 object-contain" />
                          ) : (
                            <Building2 className="h-4 w-4" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{emisor.nombre}</p>
                          <p className="text-xs text-gray-500">{emisor.identificacion}</p>
                        </div>
                        {currentEmisor?.id === emisor.id && (
                          <CheckCircle className="h-4 w-4 text-blue-500" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
  );

  // ── EN MODO SUITE NO SE DIBUJA EL ARMAZON (item 096) ──────────────────────
  //
  // Va DESPUES de todos los hooks, nunca antes: una salida temprana arriba
  // cambiaria la cantidad de hooks entre renders y React se cae. Lo unico que
  // cambia es lo que se pinta.
  //
  // `min-h-screen` NO se usa aca: el armazon lo lleva porque ocupa la ventana, y
  // adentro de un iframe eso congela el alto en el de la ventana del anfitrion
  // —que es el otro sintoma del item 096— y el contenido queda cortado o sobra
  // espacio en blanco. La clase `embebido` es la misma que usa la cascara, asi
  // que el tema que mando Unity se aplica igual.
  if (esSuite) {
    return (
      <div className="embebido" ref={contenidoSuite}>
        <Outlet />
      </div>
    );
  }

  return (
    <div className={temaNuevo ? clsx('f360 min-h-screen', marcaDeAgua && 'agua') : 'min-h-screen bg-gray-50'}>
      {soporte && (
        <div className="fixed top-0 inset-x-0 z-[60] bg-amber-500 text-white text-sm px-4 py-1.5 flex items-center justify-center gap-3 shadow">
          <span>Sesión de soporte como <strong>{soporte.email}</strong>{soporte.vence ? ` · vence ${soporte.vence.toLocaleTimeString('es-CR', { timeZone: 'America/Costa_Rica', hour: '2-digit', minute: '2-digit' })}` : ''}</span>
          <button onClick={() => { handleLogout(); window.close(); }} className="px-2 py-0.5 rounded bg-white/20 hover:bg-white/30 font-medium">Terminar</button>
        </div>
      )}
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside className={clsx(
        'fixed top-0 left-0 z-50 h-full w-72 flex flex-col transform transition-transform duration-200 ease-in-out lg:hidden',
        temaNuevo ? 'f360-lateral' : 'bg-white border-r border-gray-200',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        {SidebarContent()}
      </aside>

      {/* Desktop Sidebar — se auto-colapsa en rutas embed (iframe a pantalla completa) */}
      <aside className={clsx(
        'hidden lg:flex lg:fixed lg:top-0 lg:left-0 lg:z-50 lg:h-full lg:flex-col',
        sidebarRiel ? 'lg:w-16' : 'lg:w-72',
        temaNuevo ? 'f360-lateral' : 'lg:bg-white lg:border-r lg:border-gray-200',
        'transition-[transform,width] duration-300 ease-in-out',
        sidebarOculto ? 'lg:-translate-x-full' : 'lg:translate-x-0'
      )}>
        {sidebarRiel ? RielContent() : SidebarContent()}
        {/* Botón "ocultar menú": círculo de contorno sobre el borde derecho del menú. Solo existe con el
            menú a la vista (si no, al deslizarse el menú quedaba asomado en el borde de la pantalla). */}
        {!sidebarHidden && (
          <button
            onClick={() => { setRiel(null); toggleUserSidebar(true); }}
            className="hidden lg:flex items-center justify-center absolute top-1/2 -translate-y-1/2 -right-4 z-10 h-8 w-8 rounded-full bg-white border border-indigo-300 text-indigo-600 shadow-md hover:bg-indigo-50 hover:border-indigo-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
            title="Achicar el menú a solo íconos (más espacio)"
            aria-label="Achicar el menú"
          >
            <ChevronLeft className="h-5 w-5" strokeWidth={2.5} />
          </button>
        )}
      </aside>

      {/* Main content */}
      <div className={clsx(
        'min-w-0 overflow-x-hidden transition-[padding] duration-300 ease-in-out',
        sidebarOculto ? 'lg:pl-0' : sidebarRiel ? 'lg:pl-16' : 'lg:pl-72'
      )}>
        {temaNuevo ? (
        <header className="f360-cabecera sticky top-0 z-30 h-16 flex items-center justify-between gap-4 px-4 lg:px-8">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-gray-100" aria-label="Abrir menú">
              <Menu className="h-5 w-5" />
            </button>
            {sidebarOculto && (
              <button
                onClick={() => toggleUserSidebar(false)}
                className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-indigo-300 text-indigo-600 bg-white hover:bg-indigo-50 hover:border-indigo-500 transition-colors text-sm font-medium shrink-0"
                title="Mostrar menú de Finance 360"
              >
                <PanelLeftOpen className="h-4 w-4" />
                <span>Menú</span>
              </button>
            )}
            <span className="f360-ojo">Finance 360</span>
            <span className="text-gray-300">/</span>
            <h1 className="f360-titulo truncate">{pageStatus ? pageStatus.text : (tituloDeRuta(location.pathname)?.title || 'Dashboard')}</h1>
            {pageStatus?.subtext && <span className="f360-ojo hidden md:inline">{pageStatus.subtext}</span>}
            {!pageStatus && pageLastUpdated && (
              <span className="text-xs text-gray-400 tabular-nums" title={`Última actualización: ${dayjs(pageLastUpdated).format('DD/MM/YYYY HH:mm:ss')}`}>
                Actualizado: {dayjs(pageLastUpdated).format('HH:mm:ss')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 hidden xl:block">
              {new Date().toLocaleDateString('es-CR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'America/Costa_Rica' })}
            </span>
            <NotificationsBell />
            {selectorEmpresa}
          </div>
        </header>
        ) : (
        /* Top bar - height matches sidebar logo section (py-8 + content ~127px) */
        <header className="bg-white border-b border-gray-200 flex items-stretch justify-between px-4 lg:px-6 min-h-[127px] relative">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 self-center"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="self-center">
            {sidebarOculto && (
            <button
              onClick={() => toggleUserSidebar(false)}
              className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-indigo-300 text-indigo-600 bg-white hover:bg-indigo-50 hover:border-indigo-500 transition-colors text-sm font-medium shrink-0"
              title="Mostrar menú de Finance 360"
            >
              <PanelLeftOpen className="h-4 w-4" />
              <span>Menú</span>
            </button>
          )}
          </div>

          {/* Page status (Facturando..., etc) or Page Title */}
          {pageStatus ? (
            <div className="flex-1 h-full flex items-end justify-center pb-3">
              <div className="text-center">
                <h2 className={`text-4xl font-light tracking-wider text-transparent bg-clip-text bg-gradient-to-r opacity-50 ${
                  pageStatus.gradient === 'amber'
                    ? 'from-amber-400 via-orange-400 to-amber-400'
                    : 'from-blue-400 via-purple-400 to-blue-400'
                }`}>
                  {pageStatus.text}
                </h2>
                <p className="text-xs text-gray-400 tracking-widest uppercase">
                  {pageStatus.subtext}
                </p>
              </div>
            </div>
          ) : tituloDeRuta(location.pathname) ? (
            <div className="absolute left-1/2 -translate-x-1/2 bottom-3 flex items-center gap-3">
              {(() => {
                const PageIcon = tituloDeRuta(location.pathname).icon;
                return <PageIcon className="h-6 w-6 text-gray-400" />;
              })()}
              <h1 className="text-2xl font-semibold text-gray-700">
                {tituloDeRuta(location.pathname).title}
              </h1>
              {/* "Actualizado: HH:MM:SS" publicado por la página activa (pageMetaStore) */}
              {pageLastUpdated && (
                <span
                  className="text-xs text-gray-400 tabular-nums ml-2"
                  title={`Última actualización: ${dayjs(pageLastUpdated).format('DD/MM/YYYY HH:mm:ss')}`}
                >
                  Actualizado: {dayjs(pageLastUpdated).format('HH:mm:ss')}
                </span>
              )}
            </div>
          ) : null}
          {/* Spacer for flex layout */}
          {!pageStatus && (
            <div className="flex-1" />
          )}

          {/* Notifications bell — al lado del selector de empresa */}
          <div className="self-center mr-2">
            <NotificationsBell />
          </div>

          {/* Right side: Date + Company selector (stacked) */}
          <div className="flex flex-col items-end gap-1 self-center">
            {/* Date display */}
            <p className="text-sm text-gray-500 hidden sm:block">
              {new Date().toLocaleDateString('es-CR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
            {selectorEmpresa}

          </div>
        </header>
        )}

        {/* Page content */}
        {/* 27/10/2026: --alto-contenido es el alto útil de la pantalla (sin la cabecera ni el relleno de main). Las
            pantallas de dos paneles lo usan en vez de un número fijo, que estaba calculado para la cabecera vieja
            (127 px) y con el tema nuevo (64 px) dejaba ~100 px vacíos abajo. */}
        <main className="p-4 lg:p-6 min-w-0" style={{ '--alto-contenido': temaNuevo ? 'calc(100vh - 64px - 3rem)' : 'calc(100vh - 127px - 3rem)' }}>
          <Outlet key={currentEmisor?.id} />
        </main>
      </div>
    </div>
  );
}
